// First-party cookies that govern the landing site's ad-sharing opt-out.
//
// Contract (per compliance.md §1):
//   - `as_ad_optout` is "true" when the user (or GPC) has opted out of
//     data sharing with advertising partners. Any other value (including
//     "false" or absent) means sharing is allowed. The Pixel server-side
//     gate treats only "true" as opted-out.
//   - `as_gpc_applied` is "true" iff the middleware originally set the
//     opt-out from a `Sec-GPC: 1` header. The modal renders the GPC note
//     when this is set, regardless of whether the user has since toggled.
//
// Cross-surface sync:
//   In production the cookies are scoped to
//   `Domain=.autoscreenshot.website` so the preference holds across the
//   apex and any sibling subdomain. Dev (`NODE_ENV !== 'production'`)
//   leaves the Domain attribute off, so localhost and Railway preview
//   hosts get a normal host-scoped cookie.

export const AD_OPTOUT_COOKIE = 'as_ad_optout'
export const ANALYTICS_OPTOUT_COOKIE = 'as_analytics_optout'
export const GPC_APPLIED_COOKIE = 'as_gpc_applied'
export const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/**
 * Production-only cookie domain. `.autoscreenshot.website` lets the apex
 * and any sibling subdomain read the same preference, so a choice made
 * on one surface is honored everywhere.
 *
 * Returns `undefined` in dev so the browser falls back to host-scoped
 * cookies (which is what works on localhost / Railway preview hosts).
 */
function getCrossSurfaceCookieDomain(): string | undefined {
  if (typeof process === 'undefined') return undefined
  if (process.env.NODE_ENV !== 'production') return undefined
  return '.autoscreenshot.website'
}

/**
 * Read a cookie value from `document.cookie`. Safe to call on the server
 * (returns null when `document` is unavailable). For server reads from
 * Next.js Route Handlers / Server Components, use `cookies()` from
 * `next/headers` directly instead — this helper is the client path.
 */
function readClientCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const target = `${name}=`
  for (const entry of document.cookie.split(';')) {
    const trimmed = entry.trim()
    if (trimmed.startsWith(target)) return trimmed.slice(target.length)
  }
  return null
}

/** Browser-side helper: returns true when the user is opted out of ad
 *  sharing (the Marketing category — gates the Facebook Pixel). */
export function readAdOptOut(): boolean {
  return readClientCookie(AD_OPTOUT_COOKIE) === 'true'
}

/** Browser-side helper: returns true when the user is opted out of
 *  analytics (the Analytics category — gates PostHog capture). */
export function readAnalyticsOptOut(): boolean {
  return readClientCookie(ANALYTICS_OPTOUT_COOKIE) === 'true'
}

/** Browser-side helper: returns true when the opt-out was set via GPC. */
export function readGpcApplied(): boolean {
  return readClientCookie(GPC_APPLIED_COOKIE) === 'true'
}

/**
 * Browser-side helper: persist the user's choice.
 * `true` ⇒ opted out (Pixel will not render on next request).
 * `false` ⇒ opted in (default — Pixel renders).
 *
 * We always write a literal string so server-side reads can compare
 * unambiguously. SameSite=Lax matches our other cookies; Secure is added
 * automatically by the browser on https origins. In production, the
 * `Domain=.autoscreenshot.website` attribute carries the preference
 * across sibling surfaces (see `getCrossSurfaceCookieDomain`).
 */
export function writeAdOptOut(optedOut: boolean): void {
  writeOptOutCookie(AD_OPTOUT_COOKIE, optedOut)
}

/**
 * Browser-side helper: persist the user's analytics choice.
 * `true` ⇒ opted out (PostHog calls `opt_out_capturing`).
 * `false` ⇒ opted in (default).
 *
 * Cookie shape mirrors `as_ad_optout` exactly so the cross-surface sync
 * via `Domain=.autoscreenshot.website` in prod works the same way (both
 * opt-out signals travel together).
 */
export function writeAnalyticsOptOut(optedOut: boolean): void {
  writeOptOutCookie(ANALYTICS_OPTOUT_COOKIE, optedOut)
}

/** Shared cookie writer for the opt-out family. SameSite=Lax + Path=/ +
 *  Max-Age=1y + optional Domain=.autoscreenshot.website in prod. */
function writeOptOutCookie(name: string, optedOut: boolean): void {
  if (typeof document === 'undefined') return
  const value = optedOut ? 'true' : 'false'
  const domain = getCrossSurfaceCookieDomain()
  const parts = [
    `${name}=${value}`,
    `Max-Age=${ONE_YEAR_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ]
  if (domain) parts.push(`Domain=${domain}`)
  document.cookie = parts.join('; ')
}

/**
 * Production cookie-attribute payload used by middleware writes. Mirrors
 * `writeAdOptOut`'s prod attributes (Domain, SameSite, Path, Max-Age).
 * Kept here so the middleware doesn't have to re-derive the domain rule.
 */
export function getServerCookieAttributes(): {
  maxAge: number
  sameSite: 'lax'
  secure: boolean
  path: '/'
  domain?: string
} {
  return {
    maxAge: ONE_YEAR_SECONDS,
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    domain: getCrossSurfaceCookieDomain(),
  }
}
